import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import NewDealModal from '../deals/NewDealModal';
import ViewEditDealModal from '../deals/ViewEditDealModal';
import EmailModal from './EmailModal';
import EmailViewModal from './EmailViewModal';
import EditLeadModal from './EditLeadModal';
import EditTaskModal from '../tasks/EditTaskModal'; // Import the EditTaskModal
import LeadSearchInput from './LeadSearchInput';
import { DeleteConfirmationModal, ConfirmationModal, VoiceRecorder } from '../common';
import { setDocumentTitle, DEFAULT_TITLE } from '../../utils/DocumentTitle';
import { getCrossTabStorageKeys } from '../../utils/permissions';
import { truncateUrl } from '../../utils/truncateUrl';
import useModuleSettings from '../../hooks/useModuleSettings';

const LeadView = () => {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState('');
  const [notesHistory, setNotesHistory] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [emailHistory, setEmailHistory] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [taskHistory, setTaskHistory] = useState([]);
  const [dealHistory, setDealHistory] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [dealStageHistory, setDealStageHistory] = useState([]);
  const [mixpanelHistory, setMixpanelHistory] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [callAttempts, setCallAttempts] = useState(0);
  const [newDealModalOpen, setNewDealModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState(null);
  const [editLeadModalOpen, setEditLeadModalOpen] = useState(false);
  const [newTaskModalOpen, setNewTaskModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailViewModalOpen, setEmailViewModalOpen] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activitiesPerPage] = useState(7);
  const [salesCoachActive, setSalesCoachActive] = useState(false);
  const [salesCoachSuccess, setSalesCoachSuccess] = useState(false);
  const [analyzingCall, setAnalyzingCall] = useState(false);
  const [dealCount, setDealCount] = useState(0);
  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [dealsExpanded, setDealsExpanded] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState('');
  const [deleteEntryId, setDeleteEntryId] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [showEditTaskModal, setShowEditTaskModal] = useState(false); // State for Edit Task Modal visibility
  const [selectedTaskToEdit, setSelectedTaskToEdit] = useState(null); // State for the task being edited
  const [viewDealModalOpen, setViewDealModalOpen] = useState(false); // State for ViewEditDealModal visibility
  const [selectedDealId, setSelectedDealId] = useState(null); // State for the deal being viewed/edited
  const [intercomConversations, setIntercomConversations] = useState([]);
  const [loadingIntercom, setLoadingIntercom] = useState(false);
  const { moduleSettings } = useModuleSettings();
  const navigate = useNavigate();
  
  // Function to handle viewing/editing a deal
  const handleViewDeal = (dealId) => {
    setSelectedDealId(dealId);
    setViewDealModalOpen(true);
  };

  // Fetch Intercom conversations for this lead
  const fetchIntercomConversations = async () => {
    if (!moduleSettings.intercom) return;
    
    try {
      setLoadingIntercom(true);
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      
      const response = await axios.get(`/api/intercom/leads/${id}/conversations`, config);
      setIntercomConversations(response.data || []);
    } catch (error) {
      console.error('Error fetching Intercom conversations:', error);
      setIntercomConversations([]);
    } finally {
      setLoadingIntercom(false);
    }
  };

  // Sync Intercom conversations for this lead
  const syncIntercomConversations = async () => {
    if (!moduleSettings.intercom || !lead?.email) return;
    
    try {
      setLoadingIntercom(true);
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      
      const response = await axios.post(`/api/intercom/leads/${id}/sync`, {}, config);
      
      // Show sync results
      if (response.data.conversations > 0) {
        alert(`Synced ${response.data.conversations} conversations with ${response.data.messages} messages`);
      } else {
        alert('No new conversations found to sync');
      }
      
      // Refresh conversations
      await fetchIntercomConversations();
    } catch (error) {
      console.error('Error syncing Intercom conversations:', error);
      alert(error.response?.data?.error || 'Failed to sync conversations');
    } finally {
      setLoadingIntercom(false);
    }
  };
  
  // Fetch lead data on component mount
  // Fetch deal count for this lead
  const fetchDealCount = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/leads/counts/deals', config);
      if (res.data && res.data[id]) {
        setDealCount(res.data[id]);
      }
    } catch (error) {
      console.error('Error fetching deal count:', error);
    }
  };
  
  // Fetch deals associated with this lead
  const fetchDealsForLead = async () => {
    try {
      setLoadingDeals(true);
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        },
        params: {
          lead_id: id,
          sort_by: 'created_at',
          sort_order: 'desc'
        }
      };
      
      const response = await axios.get('/api/deals', config);
      
      // Handle response which includes pagination
      if (response.data.deals) {
        setDeals(response.data.deals || []);
      } else {
        // Fallback for backward compatibility
        setDeals(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching deals for lead:', err);
    } finally {
      setLoadingDeals(false);
    }
  };

  useEffect(() => {
    const fetchLead = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        // Fetch lead data
        const res = await axios.get(`/api/leads/${id}`, config);
        setLead(res.data);
        setNotes(res.data.notes || '');
        setCallAttempts(res.data.call_attempts || 0);
        
        // Update document title with lead name and email
        if (res.data.name && res.data.email) {
          setDocumentTitle(`${res.data.name} - ${res.data.email}`);
        }
        
        // Fetch lead notes history if available
        try {
          const notesRes = await axios.get(`/api/leads/${id}/notes-history`, config);
          if (notesRes.data && Array.isArray(notesRes.data)) {
            setNotesHistory(notesRes.data);
            // Reset to page 1 when data is loaded
            setActivityPage(1);
          }
        } catch (notesErr) {
          console.log('Notes history not available', notesErr);
          setNotesHistory([]);
        }
        
        // Fetch email history for the lead
        try {
          const emailRes = await axios.get(`/api/email/history/${id}`, config);
          if (emailRes.data && Array.isArray(emailRes.data)) {
            setEmailHistory(emailRes.data);
          }
        } catch (emailErr) {
          console.log('Email history not available', emailErr);
          setEmailHistory([]);
        }
        
        // Fetch call history for the lead
        try {
          const callRes = await axios.get(`/api/leads/${id}/call-history`, config);
          if (callRes.data && Array.isArray(callRes.data)) {
            setCallHistory(callRes.data);
          }
        } catch (callErr) {
          console.log('Call history not available', callErr);
          setCallHistory([]);
        }
        
        // Fetch task history for the lead
        try {
          const taskRes = await axios.get(`/api/tasks/lead/${id}/history`, config);
          if (taskRes.data && Array.isArray(taskRes.data)) {
            setTaskHistory(taskRes.data);
          }
        } catch (taskErr) {
          console.log('Task history not available', taskErr);
          setTaskHistory([]);
        }
        
        // Fetch deal history for the lead
        try {
          const dealRes = await axios.get(`/api/deals/lead/${id}/history`, config);
          if (dealRes.data && Array.isArray(dealRes.data)) {
            setDealHistory(dealRes.data);
          }
        } catch (dealErr) {
          console.log('Deal history not available', dealErr);
          setDealHistory([]);
        }
        
        // Fetch status history for the lead
        try {
          const statusRes = await axios.get(`/api/leads/${id}/status-history`, config);
          if (statusRes.data && Array.isArray(statusRes.data)) {
            setStatusHistory(statusRes.data);
          }
        } catch (statusErr) {
          console.log('Status history not available', statusErr);
          setStatusHistory([]);
        }
        
        // Fetch deal stage history for the lead
        try {
          const dealStageRes = await axios.get(`/api/deals/lead/${id}/stage-history`, config);
          if (dealStageRes.data && Array.isArray(dealStageRes.data)) {
            setDealStageHistory(dealStageRes.data);
          }
        } catch (dealStageErr) {
          console.log('Deal stage history not available', dealStageErr);
          setDealStageHistory([]);
        }
        
        // Fetch Mixpanel events for the lead (if module is enabled)
        try {
          const mixpanelRes = await axios.get(`/api/mixpanel-client/events/${id}`, config);
          if (mixpanelRes.data && Array.isArray(mixpanelRes.data)) {
            setMixpanelHistory(mixpanelRes.data);
          }
        } catch (mixpanelErr) {
          console.log('Mixpanel events not available (module may be disabled):', mixpanelErr);
          setMixpanelHistory([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching lead:', err);
        setError('Failed to load lead data. Please try again later.');
        setLoading(false);
      }
    };
    
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        const res = await axios.get('/api/auth/users', config);
        setUsers(res.data);
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };
    
    const getCurrentUser = () => {
      try {
        // Get current user from localStorage (stored during login/dashboard load)
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          setCurrentUser(user);
        }
      } catch (err) {
        console.error('Error getting current user:', err);
      }
    };
    
    fetchLead();
    fetchUsers();
    getCurrentUser();
    fetchDealCount();
    fetchDealsForLead();
    fetchIntercomConversations();
    
    // Reset document title when component unmounts
    return () => {
      setDocumentTitle(DEFAULT_TITLE);
    };
  }, [id]);

  // Function to fetch task history for this lead
  const fetchTaskHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const taskRes = await axios.get(`/api/tasks/lead/${id}/history`, config);
      
      if (taskRes.data && Array.isArray(taskRes.data)) {
        setTaskHistory(taskRes.data);
        console.log('Task history refreshed');
      }
    } catch (taskErr) {
      console.error('Task history refresh failed:', taskErr);
    }
  };
  
  // Function to handle clicking on a task activity item
  const handleTaskActivityClick = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const res = await axios.get(`/api/tasks/${taskId}`, config);
      setSelectedTaskToEdit(res.data); // Set the fetched task data
      setShowEditTaskModal(true); // Open the modal
    } catch (err) {
      console.error('Error fetching task details:', err);
      // Optionally show an error notification to the user
    }
  };

  const handleReplyToEmail = (email) => {
    // Close the email view modal
    setEmailViewModalOpen(false);
    // Set the email to reply to
    setReplyToEmail(email);
    // Open the email compose modal
    setEmailModalOpen(true);
  };
  
  const handleNotesChange = (e) => {
    setNotes(e.target.value);
  };
  
  const handleNewNoteChange = (e) => {
    setNewNote(e.target.value);
    
    // Auto-adjust textarea height based on content
    e.target.style.height = 'auto';
    e.target.style.height = (e.target.scrollHeight) + 'px';
  };
  
  const addNewNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Add the new note
      const res = await axios.post(
        `/api/leads/${id}/notes`,
        { content: newNote },
        config
      );
      
      // Reset the form and textarea height
      setNewNote('');
      // Reset textarea height to default (will be handled by ref on next render)
      document.querySelector('textarea[placeholder="Write a new note..."]').style.height = '80px';
      
      // Refresh notes history
      const notesRes = await axios.get(`/api/leads/${id}/notes-history`, config);
      if (notesRes.data && Array.isArray(notesRes.data)) {
        setNotesHistory(notesRes.data);
        // Reset to page 1 when new note is added
        setActivityPage(1);
      }
      
      setIsSaving(false);
    } catch (err) {
      console.error('Error adding note:', err);
      setIsSaving(false);
    }
  };
  
  const convertLegacyNote = async () => {
    if (!lead || !lead.notes) return;
    
    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Add legacy note as a new note
      await axios.post(
        `/api/leads/${id}/notes`,
        { content: lead.notes },
        config
      );
      
      // Clear the legacy notes field
      await axios.patch(
        `/api/leads/${id}/notes`,
        { notes: '' },
        config
      );
      
      // Update the lead state
      setLead({ ...lead, notes: '' });
      setNotes('');
      setIsEditing(false);
      
      // Refresh notes history
      try {
        const notesRes = await axios.get(`/api/leads/${id}/notes-history`, config);
        if (notesRes.data && Array.isArray(notesRes.data)) {
          setNotesHistory(notesRes.data);
        }
      } catch (notesErr) {
        console.log('Notes history not available', notesErr);
      }
      
      // Refresh email history
      try {
        const emailRes = await axios.get(`/api/email/history/${id}`, config);
        if (emailRes.data && Array.isArray(emailRes.data)) {
          setEmailHistory(emailRes.data);
        }
      } catch (emailErr) {
        console.log('Email history not available', emailErr);
      }
      
    } catch (err) {
      console.error('Error saving notes:', err);
      setIsSaving(false);
    }
  };
  
  const updateCallAttempts = async (change) => {
    // Prevent decrementing below 0
    if (callAttempts + change < 0) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      const newCount = callAttempts + change;
      setCallAttempts(newCount); // Update UI immediately
      
      await axios.patch(
        `/api/leads/${id}/call-attempts`,
        { increment: change > 0 },
        config
      );
      
      // Update the lead state
      setLead({ ...lead, call_attempts: newCount });
      
      // If we're incrementing, refresh call history
      if (change > 0) {
        try {
          const callRes = await axios.get(`/api/leads/${id}/call-history`, config);
          if (callRes.data && Array.isArray(callRes.data)) {
            setCallHistory(callRes.data);
            // Reset to first page when new call is logged
            setActivityPage(1);
          }
        } catch (callErr) {
          console.log('Error refreshing call history:', callErr);
        }
      }
      
    } catch (err) {
      console.error('Error updating call attempts:', err);
      // Revert UI if the API call fails
      setCallAttempts(lead.call_attempts || 0);
    }
  };
  
  // Show delete confirmation modal
  const confirmDeleteEntry = (type, entryId) => {
    if (!entryId || !['email', 'call', 'note', 'status'].includes(type)) {
      console.error('Invalid entry type or ID');
      return;
    }
    
    let message = 'Are you sure you want to delete this activity entry?';
    if (type === 'note') {
      message = 'Are you sure you want to delete this note?';
    } else if (type === 'email') {
      message = 'Are you sure you want to delete this email record?';
    } else if (type === 'call') {
      message = 'Are you sure you want to delete this call record?';
    } else if (type === 'status') {
      message = 'Are you sure you want to delete this status change record?';
    }
    
    setDeleteType(type);
    setDeleteEntryId(entryId);
    setDeleteMessage(message);
    setDeleteModalOpen(true);
  };
  
  // Handle note deletion
  const deleteNote = async (noteId) => {
    confirmDeleteEntry('note', noteId);
  };

  // Execute deletion after confirmation
  const executeDeleteAction = async () => {
    if (!deleteEntryId || !deleteType) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      let success = false;
      
      // Call the appropriate API endpoint based on activity type
      if (deleteType === 'email') {
        const res = await axios.delete(`/api/email/history/${id}/${deleteEntryId}`, config);
        success = res.status === 200;
        if (success) {
          setEmailHistory(emailHistory.filter(email => email.id !== deleteEntryId));
        }
      } else if (deleteType === 'call') {
        const res = await axios.delete(`/api/leads/${id}/call-history/${deleteEntryId}`, config);
        success = res.status === 200;
        if (success) {
          setCallHistory(callHistory.filter(call => call.id !== deleteEntryId));
        }
      } else if (deleteType === 'note') {
        // Use the existing note deletion endpoint
        const res = await axios.delete(`/api/leads/${id}/notes/${deleteEntryId}`, config);
        success = res.status === 200;
        if (success) {
          setNotesHistory(notesHistory.filter(note => note.id !== deleteEntryId));
          
          // Refresh notes history (especially for the note-specific function)
          const notesRes = await axios.get(`/api/leads/${id}/notes-history`, config);
          if (notesRes.data && Array.isArray(notesRes.data)) {
            setNotesHistory(notesRes.data);
          }
        }
      } else if (deleteType === 'status') {
        // Delete status change entry
        const res = await axios.delete(`/api/leads/${id}/status-history/${deleteEntryId}`, config);
        success = res.status === 200;
        if (success) {
          setStatusHistory(statusHistory.filter(status => status.id !== deleteEntryId));
        }
      }
      
      // Clear the deletion state
      setDeleteType('');
      setDeleteEntryId(null);
      setDeleteModalOpen(false);
      
    } catch (error) {
      console.error(`Error deleting ${deleteType}:`, error);
      if (error.response?.data?.message) {
        // Could be replaced with a toast notification
        alert(error.response.data.message);
      } else {
        alert('Failed to delete entry. You may not have permission to perform this action.');
      }
      
      // Always close the modal even if there's an error
      setDeleteModalOpen(false);
    }
  };
  
  // Keep the old function signature for compatibility
  const deleteActivityEntry = (type, entryId) => {
    confirmDeleteEntry(type, entryId);
  };

  const updateLeadStatus = async (newStatus) => {
    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Check if this is a reconversion
      // (if status is changing to 'converted' and the lead was previously marked as converted)
      const isReconversion = newStatus === 'converted' && 
        lead.status !== 'converted' && 
        lead.status !== 'new' &&
        lead.reconversion_date === null;
      
      // Prepare update data
      const updateData = { 
        ...lead, 
        status: newStatus 
      };
      
      // If it's a reconversion, set the reconversion_date
      if (isReconversion) {
        updateData.reconversion_date = new Date().toISOString();
      }
      
      // Update the lead
      await axios.put(
        `/api/leads/${id}`,
        updateData,
        config
      );
      
      // Update the lead state
      const updatedLead = {
        ...lead, 
        status: newStatus,
        reconversion_date: isReconversion ? new Date().toISOString() : lead.reconversion_date
      };
      setLead(updatedLead);
      setIsSaving(false);
      
      // Notify other tabs about the lead update
      const storageKeys = getCrossTabStorageKeys();
      localStorage.setItem(storageKeys.LEAD_UPDATED, JSON.stringify({
        id: lead.id,
        timestamp: new Date().getTime()
      }));
      
      // Fetch status change history to update activity feed
      try {
        // Delay slightly to ensure the status change is fully processed
        setTimeout(async () => {
          const statusRes = await axios.get(`/api/leads/${id}/status-history`, config);
          if (statusRes.data && Array.isArray(statusRes.data)) {
            setStatusHistory(statusRes.data);
            // Reset to first page when status is changed
            setActivityPage(1);
          }
        }, 500);
      } catch (statusErr) {
        console.log('Status history not available', statusErr);
      }
      
    } catch (err) {
      console.error('Error updating lead status:', err);
      setIsSaving(false);
    }
  };
  
  // Get the assigned user's name
  const getAssignedUserName = () => {
    if (!lead?.assigned_to) return 'Unassigned';
    const user = users.find(u => u.id === lead.assigned_to);
    return user ? user.name : 'Unknown User';
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'connected':
        return 'bg-yellow-100 text-yellow-800';
      case 'HOT':
        return 'bg-red-100 text-red-800';
      case 'disqualified':
        return 'bg-gray-100 text-gray-800';
      case 'converted':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // State for error modal
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  
  // Handle lead deletion
  const deleteLead = async () => {
    try {
      setIsDeleting(true);
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete(`/api/leads/${id}`, config);
      
      // Navigate back to leads list
      window.location.href = '/leads';
    } catch (err) {
      console.error('Error deleting lead:', err);
      
      // Get the error message from the response
      const errorMessage = err.response?.data?.message || 'Failed to delete lead';
      
      // Show the error in a modal instead of just setting the error state
      setErrorModalMessage(errorMessage);
      setShowErrorModal(true);
      setIsDeleting(false);
    } finally {
      setShowDeleteConfirm(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!lead) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 m-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">Lead not found</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center">
          <Link 
            to="/leads" 
            className="mr-3 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Leads
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Lead Details</h1>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setSalesCoachActive(prev => !prev)}
            className={`${salesCoachActive ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'} text-white font-semibold py-2 px-4 rounded shadow flex items-center`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
            {salesCoachActive ? 'Close AI Coach' : 'AI Sales Coach 🤠'}
          </button>

          <button
            onClick={() => setEditLeadModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded shadow flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Edit Lead
          </button>
          
          {/* Delete Button - Only visible to superusers */}
          {currentUser && currentUser.role === 'superuser' && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded shadow flex items-center"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Lead
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      {/* AI Sales Coach Section - Shown when active */}
      {salesCoachActive && (
        <div className="mb-6 bg-white shadow rounded-lg border-l-4 border-purple-600">
          <div className="p-4">
            <div className="flex items-center mb-3">
              <span role="img" aria-label="sales coach" className="text-xl mr-2">🤠</span>
              <h2 className="text-lg font-semibold text-gray-900">AI Sales Coach</h2>
              <span className="ml-2 text-sm text-purple-600">(automatically generates live feedback + analysis)</span>
            </div>
            
            {/* Analysis in progress indicator */}
            {analyzingCall && (
              <div className="mb-4 flex items-center p-2 bg-blue-50 text-blue-700 rounded-md border border-blue-200">
                <div className="mr-2">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                </div>
                <span>
                  <strong>Analyzing call...</strong> Generating comprehensive sales coaching feedback.
                </span>
              </div>
            )}
            
            {/* Success message when coaching is completed */}
            {salesCoachSuccess && (
              <div className="mb-4 flex items-center p-2 bg-green-50 text-green-700 rounded-md border border-green-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>
                  <strong>Analysis complete!</strong> Coaching feedback has been added to this lead's notes.
                </span>
              </div>
            )}
            
            <VoiceRecorder 
              onStopRecording={() => {
                // Show analyzing indicator when recording stops (before analysis is ready)
                setAnalyzingCall(true);
              }}
              onTranscriptionComplete={(text, coaching) => {
                console.log('Coaching completed for lead:', lead.name);
                
                // Hide analyzing indicator
                setAnalyzingCall(false);
                
                // If coaching was generated, add it as a note to the lead
                if (coaching && text) {
                  const token = localStorage.getItem('token');
                  
                  // Create a well-formatted note with the transcription and coaching
                  const noteContent = `
## AI Sales Coach Analysis
                
### Transcript:
${text}

### Coaching Feedback:
${coaching}

*Generated by AI Sales Coach - ${new Date().toLocaleString()}*
`;
                  
                  // Add as a lead note
                  axios.post(`/api/leads/${id}/notes`, 
                    { content: noteContent },
                    {
                      headers: {
                        'Content-Type': 'application/json',
                        'x-auth-token': token
                      }
                    }
                  ).then(res => {
                    // Show success message
                    setSalesCoachSuccess(true);
                    
                    // Hide success message after 5 seconds
                    setTimeout(() => {
                      setSalesCoachSuccess(false);
                    }, 5000);
                    
                    // Refresh notes history to show the new coaching note
                    axios.get(`/api/leads/${id}/notes-history`, {
                      headers: { 'x-auth-token': token }
                    }).then(notesRes => {
                      if (notesRes.data && Array.isArray(notesRes.data)) {
                        setNotesHistory(notesRes.data);
                      }
                    });
                  }).catch(err => {
                    console.error('Error saving coaching as note:', err);
                  });
                }
              }} 
            />
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main Lead Info */}
        <div className="md:col-span-2">
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {lead.name}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Created on {formatDate(lead.created_at)}
                  {lead.reconversion_date && (
                    <span className="ml-2 text-orange-600">
                      • Reconverted on {formatDate(lead.reconversion_date)}
                    </span>
                  )}
                </p>
              </div>
              <div className="relative inline-flex items-center">
                <select
                  value={lead.status}
                  onChange={(e) => updateLeadStatus(e.target.value)}
                  className={`pl-2 pr-6 py-1 text-xs leading-5 font-semibold rounded-full cursor-pointer ${getStatusBadgeColor(lead.status)} border-none focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none`}
                  title="Click to change status"
                  disabled={isSaving}
                >
                  <option value="new">New</option>
                  <option value="connected">Connected</option>
                  <option value="HOT">HOT 🔥</option>
                  <option value="unreachable">Unreachable</option>
                  <option value="disqualified">Disqualified</option>
                  <option value="converted">Converted</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5">
                  <svg className="h-3 w-3 opacity-70" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
                {isSaving && (
                  <div className="absolute right-8 top-1">
                    <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="flex items-center">
                      <button
                        onClick={() => setEmailModalOpen(true)}
                        className="text-blue-500 hover:text-blue-700 mr-2"
                        title="Send email"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </button>
                      <span className="text-blue-600">
                        {lead.email}
                      </span>
                    </div>
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Secondary Email</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {lead.secondary_email ? (
                      <span className="text-blue-600">
                        {lead.secondary_email}
                      </span>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <a 
                      href={`tel:${lead.phone}`} 
                      className="text-blue-600 hover:text-blue-800"
                      onClick={(e) => {
                        // Don't prevent default so the tel: protocol still works
                        // Increment call attempts
                        updateCallAttempts(1);
                      }}
                    >
                      {lead.phone}
                    </a>
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Secondary Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {lead.secondary_phone ? (
                      <a 
                        href={`tel:${lead.secondary_phone}`} 
                        className="text-blue-600 hover:text-blue-800"
                        onClick={(e) => {
                          // Don't prevent default so the tel: protocol still works
                          // Increment call attempts
                          updateCallAttempts(1);
                        }}
                      >
                        {lead.secondary_phone}
                      </a>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Country</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {lead.country || 'Not specified'}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Company</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {lead.company || 'Not specified'}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Role</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {lead.role || 'Not specified'}
                  </dd>
                </div>
                {/* Address Display - Moved here */}
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 whitespace-pre-wrap">
                    {lead.address || 'N/A'}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"> {/* Changed background for alternation */}
                  <dt className="text-sm font-medium text-gray-500">Original Source</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <span title={lead.original_source} className="truncate block">
                      {truncateUrl(lead.original_source, 50).text || 'Not specified'}
                    </span>
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"> {/* Changed background for alternation */}
                  <dt className="text-sm font-medium text-gray-500">Latest Source</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <span title={lead.latest_source} className="truncate block">
                      {truncateUrl(lead.latest_source, 50).text || 'Not specified'}
                    </span>
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"> {/* Changed background for alternation */}
                  <dt className="text-sm font-medium text-gray-500">Last Worked</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {lead.last_worked_date ? formatDate(lead.last_worked_date) : 'Not yet worked'}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"> {/* Changed background for alternation */}
                  <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {getAssignedUserName()}
                  </dd>
                </div>

                {/* Associated Deals */}
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"> {/* Changed background for alternation */}
                  <dt className="text-sm font-medium text-gray-500">Associated Deals</dt>
                  <dd className="mt-1 text-sm font-medium text-blue-600 sm:mt-0 sm:col-span-2">
                    <button 
                      onClick={() => setDealsExpanded(prev => !prev)} 
                      className="flex items-center cursor-pointer focus:outline-none"
                    >
                      {dealCount > 0 ? (
                        <>
                          <span>{dealCount}</span>
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className={`ml-2 h-4 w-4 transition-transform ${dealsExpanded ? 'transform rotate-180' : ''}`}
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </>
                      ) : '0'}
                    </button>
                  </dd>
                </div>
                
                {/* Deals List - Appears when expanded */}
                {dealsExpanded && dealCount > 0 && (
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Courses & Deals</h4>
                    {loadingDeals ? (
                      <div className="flex justify-center py-3">
                        <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      </div>
                    ) : deals.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                              <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {deals.map(deal => (
                              <tr key={deal.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{deal.title || 'No title'}</div>
                                  <div className="text-xs text-gray-500">{deal.course || 'No course specified'}</div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {new Intl.NumberFormat('en-US', { 
                                      style: 'currency', 
                                      currency: deal.currency || 'USD' 
                                    }).format(deal.value || 0)}
                                  </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                                    ${deal.stage_name === 'Payment Received' ? 'bg-green-100 text-green-800' :
                                      deal.stage_name === 'Awaiting Payment' ? 'bg-yellow-100 text-yellow-800' :
                                      deal.stage_name === 'Refunded' ? 'bg-red-100 text-red-800' :
                                      'bg-blue-100 text-blue-800'}`}>
                                    {deal.stage_name || 'Unknown Status'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(deal.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                                  <button 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleViewDeal(deal.id);
                                    }}
                                    className="text-blue-600 hover:text-blue-900 mr-3 cursor-pointer"
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 py-2">No deals found for this lead.</p>
                    )}
                  </div>
                )}
                
                {/* Call Attempts */}
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"> {/* Kept background white as it follows gray */}
                  <dt className="text-sm font-medium text-gray-500">Call Attempts</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="flex items-center">
                      <span className="text-lg font-semibold mr-4">{callAttempts}</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => updateCallAttempts(-1)}
                          className={`inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white ${
                            callAttempts > 0 
                              ? 'bg-red-600 hover:bg-red-700' 
                              : 'bg-gray-400 cursor-not-allowed'
                          } focus:outline-none`}
                          disabled={callAttempts === 0}
                          title="Decrement call attempts"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          onClick={() => updateCallAttempts(1)}
                          className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                          title="Increment call attempts"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </dd>
                </div>
                
                {/* Email Subscription Status */}
                {lead.subscribed !== undefined && (
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Email Subscription</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {lead.subscribed ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Subscribed
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                          Unsubscribed
                        </span>
                      )}
                    </dd>
                  </div>
                )}
                
                {/* Notes */}
                <div className="bg-white px-4 py-5 sm:px-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-medium text-gray-900">Notes</h3>
                  </div>
                  
                  {/* Legacy Note Conversion - show only if there's a legacy note */}
                  {notes && (
                    <div className="border-b border-gray-200 pb-4 mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-xs font-medium text-gray-500">Legacy Note</h4>
                        <button
                          onClick={convertLegacyNote}
                          disabled={isSaving}
                          className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
                        >
                          {isSaving ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Converting...
                            </>
                          ) : 'Convert to New Format'}
                        </button>
                      </div>
                      <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded whitespace-pre-wrap">
                        {notes}
                      </div>
                      <p className="mt-2 text-xs text-gray-500 italic">
                        This note is in the legacy format. Click "Convert to New Format" to move it to the new notes system.
                      </p>
                    </div>
                  )}
                  
                  {/* New Note Form */}
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-gray-500 mb-2">Add New Note</h4>
                    <div className="flex flex-col space-y-2">
                      <textarea
                        value={newNote}
                        onChange={handleNewNoteChange}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 overflow-hidden"
                        rows="3"
                        style={{ minHeight: '80px', resize: 'none' }}
                        placeholder="Write a new note..."
                        ref={node => {
                          // Initialize height when first rendered
                          if (node) {
                            node.style.height = 'auto';
                            node.style.height = node.scrollHeight + 'px';
                          }
                        }}
                      ></textarea>
                      <div className="flex justify-end">
                        <button
                          onClick={addNewNote}
                          disabled={isSaving || !newNote.trim()}
                          className={`px-3 py-1 rounded text-white text-sm flex items-center ${
                            isSaving || !newNote.trim() ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          {isSaving ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Adding...
                            </>
                          ) : 'Add Note'}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Notes History */}
                  {notesHistory.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 mb-2">Notes History</h4>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {notesHistory.map((note) => (
                          <div key={note.id} className="bg-gray-50 p-3 rounded relative group">
                            <div className="flex justify-between items-start mb-1">
                              <span className="text-xs font-medium text-gray-900">
                                {note.user_name || 'Unknown User'}
                              </span>
                              <div className="flex items-center space-x-1">
                                <span className="text-xs text-gray-500">
                                  {new Date(note.timestamp).toLocaleString()}
                                </span>
                                {/* Delete button - only visible on hover */}
                                {currentUser && 
                                  (currentUser.id === note.user_id || 
                                   ['admin', 'superuser'].includes(currentUser.role)) && (
                                  <button
                                    onClick={() => deleteNote(note.id)}
                                    className="invisible group-hover:visible text-gray-400 hover:text-red-500 ml-2"
                                    title="Delete note"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="text-sm text-gray-900 whitespace-pre-wrap">
                              {note.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Intercom Conversations */}
                  {moduleSettings.intercom && (
                    <div className={notesHistory.length > 0 ? "mt-6" : ""}>
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-xs font-medium text-gray-500">Intercom Conversations</h4>
                        <div className="flex items-center space-x-2">
                          {loadingIntercom && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                          )}
                          <button
                            onClick={syncIntercomConversations}
                            disabled={loadingIntercom || !lead?.email}
                            className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                            title={!lead?.email ? "Lead must have an email to sync conversations" : "Sync conversations from Intercom"}
                          >
                            Sync
                          </button>
                        </div>
                      </div>
                      
                      {intercomConversations.length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {intercomConversations.map((conversation) => (
                            <div key={conversation.id} className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs font-medium text-blue-900">
                                    Intercom Conversation
                                  </span>
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    conversation.state === 'open' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {conversation.state}
                                  </span>
                                </div>
                                <span className="text-xs text-blue-700">
                                  {new Date(conversation.updated_at_intercom).toLocaleString()}
                                </span>
                              </div>
                              
                              {conversation.assignee_name && (
                                <div className="text-xs text-blue-700 mb-2">
                                  Assigned to: {conversation.assignee_name}
                                </div>
                              )}
                              
                              <div className="text-xs text-blue-600 mb-2">
                                {conversation.conversation_message_count} messages
                              </div>
                              
                              {conversation.messages && conversation.messages.length > 0 && (
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                  {conversation.messages.slice(0, 3).map((message, index) => (
                                    <div key={message.id || index} className="bg-white p-2 rounded text-xs">
                                      <div className="flex justify-between items-start mb-1">
                                        <span className="font-medium text-gray-900">
                                          {message.author_name || message.author_type}
                                        </span>
                                        <span className="text-gray-500">
                                          {new Date(message.created_at_intercom).toLocaleString()}
                                        </span>
                                      </div>
                                      <div className="text-gray-700" dangerouslySetInnerHTML={{ 
                                        __html: message.body?.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') || 'No content'
                                      }} />
                                    </div>
                                  ))}
                                  {conversation.messages.length > 3 && (
                                    <div className="text-xs text-blue-600 text-center py-1">
                                      + {conversation.messages.length - 3} more messages
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 py-4 text-center">
                          {loadingIntercom ? 'Loading conversations...' : 
                           !lead?.email ? 'Lead needs an email address to sync Intercom conversations' :
                           'No Intercom conversations found. Click "Sync" to fetch from Intercom.'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </dl>
            </div>
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="md:col-span-1 space-y-6">
          {/* Recent Activity */}
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Recent Activity
              </h3>
            </div>
            <div className="border-t border-gray-200 p-0">
              <ul className="divide-y divide-gray-200">
                {(notesHistory.length > 0 || emailHistory.length > 0 || callHistory.length > 0 || taskHistory.length > 0 || dealHistory.length > 0 || dealStageHistory.length > 0 || mixpanelHistory.length > 0) ? (
                  // Combine and sort all activity types by date
                  (() => {
                    const allActivities = [
                      ...notesHistory.map(entry => ({
                        type: 'note',
                        timestamp: entry.timestamp,
                        user_name: entry.user_name || 'Unknown User',
                        data: entry,
                        id: entry.id
                      })), 
                      ...emailHistory.map(email => ({
                        type: 'email',
                        timestamp: email.created_at,
                        user_name: email.sender_name || 'Unknown User',
                        data: email,
                        id: email.id
                      })),
                      ...callHistory.map(call => ({
                        type: 'call',
                        timestamp: call.call_time,
                        user_name: call.user_name || 'Unknown User',
                        data: call,
                        id: call.id
                      })),
                      ...taskHistory.map(task => ({
                        type: 'task',
                        timestamp: task.timestamp,
                        user_name: task.user_name || 'Unknown User',
                        data: task,
                        id: task.id
                      })),
                      ...dealHistory.map(deal => ({
                        type: 'deal',
                        timestamp: deal.timestamp,
                        user_name: deal.user_name || deal.owner_name || 'Unknown User',
                        data: deal,
                        id: deal.id
                      })),
                      ...statusHistory.map(status => ({
                        type: 'status',
                        timestamp: status.created_at,
                        user_name: status.user_name || 'Unknown User',
                        data: status,
                        id: status.id
                      })),
                      ...dealStageHistory.map(stage => ({
                        type: 'deal_stage',
                        timestamp: stage.timestamp,
                        user_name: stage.user_name || 'Unknown User',
                        data: stage,
                        id: stage.id
                      })),
                      ...mixpanelHistory.map(event => ({
                        type: 'mixpanel',
                        timestamp: event.timestamp,
                        user_name: event.user_name || 'Website Visitor',
                        data: event.data,
                        id: event.id
                      }))
                    ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                    
                    // Calculate pagination
                    const totalPages = Math.ceil(allActivities.length / activitiesPerPage);
                    // Get current page activities
                    const indexOfLastActivity = activityPage * activitiesPerPage;
                    const indexOfFirstActivity = indexOfLastActivity - activitiesPerPage;
                    const currentActivities = allActivities.slice(indexOfFirstActivity, indexOfLastActivity);
                    
                    return (
                      <>
                        {/* Activities List */}
                        {currentActivities.map((activity, index) => (
                    <li 
                      key={index} 
                      className={`px-4 py-3 group relative hover:bg-gray-50 ${
                        activity.type === 'email' || activity.type === 'task' || activity.type === 'deal' ? 'cursor-pointer' : '' // Add cursor pointer for clickable items
                      }`}
                      onClick={
                        activity.type === 'email' ? () => {
                          setSelectedEmail(activity.data);
                          setEmailViewModalOpen(true);
                        } : activity.type === 'task' ? () => {
                          handleTaskActivityClick(activity.id); // Call handler for tasks
                        } : activity.type === 'deal' ? () => {
                          handleViewDeal(activity.id); // Call handler for deals
                        } : undefined
                      }
                    >
                      <div className="flex justify-between">
                        <p className="text-sm font-medium text-gray-900 flex items-center">
                          {activity.type === 'note' ? (
                            <>Notes Updated</>
                          ) : activity.type === 'email' ? (
                            <>
                              {activity.data.direction === 'incoming' ? (
                                <>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                  </svg>
                                  Email Received
                                </>
                              ) : (
                                <>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                  </svg>
                                  Email Sent
                                </>
                              )}
                            </>
                          ) : activity.type === 'call' ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                              </svg>
                              Call Attempt
                            </>
                          ) : activity.type === 'task' ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
                              </svg>
                              Task Created
                            </>
                          ) : activity.type === 'deal' ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-purple-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              Deal Created
                            </>
                          ) : activity.type === 'status' ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-indigo-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              Status Changed
                            </>
                          ) : activity.type === 'deal_stage' ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-purple-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                              </svg>
                              Deal Stage Changed
                            </>
                          ) : activity.type === 'mixpanel' ? (
                            <>
                              <img src="/icons/mixpanel.svg" alt="Mixpanel" className="h-4 w-4 mr-1" />
                              {activity.data.event || 'Website Activity'}
                            </>
                          ) : (
                            <>Unknown Activity</>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(activity.timestamp)}
                        </p>

                        {/* Delete button - Only visible on hover and for authorized users */}
                        {currentUser && (
                          (activity.type === 'note' && (currentUser.id === activity.data.user_id || ['admin', 'superuser'].includes(currentUser.role))) || 
                          (activity.type !== 'note' && ['admin', 'superuser'].includes(currentUser.role)) 
                        ) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent opening the email modal when clicking delete
                              deleteActivityEntry(activity.type, activity.id);
                            }}
                            className="absolute right-2 top-2 invisible group-hover:visible text-gray-400 hover:text-red-500"
                            title="Delete entry"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {activity.type === 'mixpanel' ? 'Website Activity' : `by ${activity.user_name}`}
                      </p>
                      {activity.type === 'email' && (
                        <div className="mt-1">
                          <p className="text-xs text-gray-600 truncate">
                            Subject: {activity.data.subject}
                          </p>
                          <p className={`text-xs mt-1 ${activity.data.direction === 'incoming' ? 'text-green-600' : 'text-blue-600'}`}>
                            {activity.data.direction === 'incoming' ? '↩ Reply received' : 'Click to view details →'}
                          </p>
                        </div>
                      )}
                      {activity.type === 'call' && activity.data.notes && (
                        <p className="text-xs text-gray-600 mt-1 truncate">
                          Notes: {activity.data.notes}
                        </p>
                      )}
                      {activity.type === 'task' && (
                        <div className="mt-1">
                          <p className="text-xs text-gray-600 truncate">
                            <span className="font-medium">{activity.data.title}</span> 
                            {activity.data.description && ` - ${activity.data.description}`}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            <span className={`px-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                              activity.data.priority === 'high' ? 'bg-red-100 text-red-800' :
                              activity.data.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {activity.data.priority}
                            </span>
                            <span className="ml-2">
                              Status: {activity.data.status}
                            </span>
                            <span className="ml-2 text-blue-500 group-hover:underline">(Click to edit)</span> {/* Add edit hint */}
                          </p>
                        </div>
                      )}
                      {activity.type === 'deal' && (
                        <div className="mt-1">
                          <p className="text-xs text-gray-600 truncate">
                            <span className="font-medium">{activity.data.title}</span>
                            {activity.data.course && ` - ${activity.data.course}`}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            <span className={`px-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                              activity.data.stage_name === 'Payment Received' ? 'bg-green-100 text-green-800' :
                              activity.data.stage_name === 'Awaiting Payment' ? 'bg-yellow-100 text-yellow-800' :
                              activity.data.stage_name === 'Refunded' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {activity.data.stage_name}
                            </span>
                            <span className="ml-2">
                              Value: {new Intl.NumberFormat('en-US', { 
                                style: 'currency', 
                                currency: activity.data.currency || 'USD' 
                              }).format(activity.data.value || 0)}
                            </span>
                          </p>
                          <p className="text-xs text-blue-500 mt-1 group-hover:underline">Click to view/edit deal</p>
                        </div>
                      )}
                      {activity.type === 'status' && (
                        <div className="mt-1">
                          <p className="text-xs text-gray-600">
                            Status changed from <span className={`px-1 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadgeColor(activity.data.previous_status)}`}>{activity.data.previous_status || 'None'}</span> to <span className={`px-1 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadgeColor(activity.data.new_status)}`}>{activity.data.new_status}</span>
                          </p>
                        </div>
                      )}
                      {activity.type === 'deal_stage' && (
                        <div className="mt-1">
                          <p className="text-xs text-gray-600">
                            <span className="font-medium">{activity.data.deal_title}</span> stage changed to <span className="px-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-blue-100 text-blue-800">{activity.data.new_stage_name}</span>
                            {activity.data.previous_stage_name && ` from ${activity.data.previous_stage_name}`}
                          </p>
                        </div>
                      )}
                      {activity.type === 'mixpanel' && (
                        <div className="mt-1">
                          <p className="text-xs text-gray-600 truncate">
                            <span className="font-medium">{activity.data.event}</span>
                            {activity.data.properties.page_title && ` - ${activity.data.properties.page_title}`}
                          </p>
                          {activity.data.properties.url && (
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              URL: {activity.data.properties.url}
                            </p>
                          )}
                          {(activity.data.properties.utm_source || activity.data.properties.utm_campaign) && (
                            <p className="text-xs text-gray-500 mt-1">
                              {activity.data.properties.utm_source && `Source: ${activity.data.properties.utm_source}`}
                              {activity.data.properties.utm_campaign && ` Campaign: ${activity.data.properties.utm_campaign}`}
                            </p>
                          )}
                        </div>
                      )}
                    </li>
                        ))}

                        {/* Pagination controls */}
                        {totalPages > 1 && (
                          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                            <div className="flex-1 flex justify-between items-center">
                              <button
                                onClick={() => setActivityPage(prev => Math.max(prev - 1, 1))}
                                disabled={activityPage === 1}
                                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                  activityPage === 1 
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                Previous
                              </button>
                              <span className="text-sm text-gray-700">
                                Page {activityPage} of {totalPages}
                              </span>
                              <button
                                onClick={() => setActivityPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={activityPage === totalPages}
                                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                  activityPage === totalPages 
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <li className="px-4 py-4 text-sm text-gray-500 text-center">
                    No recent activity.
                  </li>
                )}
              </ul>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Quick Actions
              </h3>
            </div>
            <div className="border-t border-gray-200 p-4 space-y-3">
              <button
                onClick={() => setEmailModalOpen(true)}
                className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 px-4 rounded flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                Send Email
              </button>
              
              <button
                onClick={() => {
                  // First open the phone dialer
                  window.open(`tel:${lead.phone}`, '_blank');
                  
                  // Then increment the call attempts counter
                  updateCallAttempts(1);
                }}
                className="w-full bg-green-50 hover:bg-green-100 text-green-700 py-2 px-4 rounded flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                Call
              </button>
              
              <button
                onClick={() => setNewDealModalOpen(true)}
                className="w-full bg-purple-50 hover:bg-purple-100 text-purple-700 py-2 px-4 rounded flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clipRule="evenodd" />
                </svg>
                New Deal
              </button>
              
              <button
                onClick={() => setNewTaskModalOpen(true)}
                className="w-full bg-amber-50 hover:bg-amber-100 text-amber-700 py-2 px-4 rounded flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                </svg>
                New Task
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* New Deal Modal */}
      {newDealModalOpen && lead && (
        <NewDealModal 
          isOpen={newDealModalOpen}
          onClose={() => setNewDealModalOpen(false)}
          onDealCreated={() => {
            // Refresh deal history after a new deal is created
            const fetchDealHistory = async () => {
              try {
                const token = localStorage.getItem('token');
                
                const config = {
                  headers: {
                    'x-auth-token': token
                  }
                };
                
                // Refresh deal count and deals
                fetchDealCount();
                fetchDealsForLead();
                
                // Refresh deal history for activity section
                const dealRes = await axios.get(`/api/deals/lead/${id}/history`, config);
                if (dealRes.data && Array.isArray(dealRes.data)) {
                  setDealHistory(dealRes.data);
                  // Reset to first page when new deal is added
                  setActivityPage(1);
                }
                
                // Refresh deal stage history for activity section
                const dealStageRes = await axios.get(`/api/deals/lead/${id}/stage-history`, config);
                if (dealStageRes.data && Array.isArray(dealStageRes.data)) {
                  setDealStageHistory(dealStageRes.data);
                }
              } catch (err) {
                console.log('Error refreshing deal history', err);
              }
            };
            
            fetchDealHistory();
          }}
          prefillData={{
            lead_id: lead.id,
            customer: lead.name,
            country: lead.country || ''
          }}
        />
      )}
      
      {/* Email Modal */}
      {emailModalOpen && lead && (
        <EmailModal
          isOpen={emailModalOpen}
          onClose={(success) => {
            setEmailModalOpen(false);
            // Reset reply data when modal closes
            setReplyToEmail(null);
            
            // If email was sent successfully, refresh email history
            if (success) {
              const fetchEmailHistory = async () => {
                try {
                  const token = localStorage.getItem('token');
                  
                  const config = {
                    headers: {
                      'x-auth-token': token
                    }
                  };
                  
                  const emailRes = await axios.get(`/api/email/history/${id}`, config);
                  if (emailRes.data && Array.isArray(emailRes.data)) {
                    setEmailHistory(emailRes.data);
                    // Reset to first page when new email is sent
                    setActivityPage(1);
                  }
                } catch (emailErr) {
                  console.log('Email history not available', emailErr);
                }
              };
              
              fetchEmailHistory();
            }
          }}
          lead={lead}
          replyToEmail={replyToEmail}
        />
      )}
      
      {/* Delete Lead Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={deleteLead}
        message="Are you sure you want to delete this lead? This action cannot be undone. All data associated with this lead will be permanently removed."
        title="Delete Lead"
      />
      
      {/* Edit Lead Modal */}
      {editLeadModalOpen && lead && (
        <EditLeadModal
          isOpen={editLeadModalOpen}
          onClose={(success) => {
            setEditLeadModalOpen(false);
            
            // If lead was updated successfully, refresh the lead data
            if (success) {
              const fetchLead = async () => {
                try {
                  setLoading(true);
                  const token = localStorage.getItem('token');
                  
                  const config = {
                    headers: {
                      'x-auth-token': token
                    }
                  };
                  
                  // Fetch lead data
                  const res = await axios.get(`/api/leads/${id}`, config);
                  setLead(res.data);
                  setNotes(res.data.notes || '');
                  setCallAttempts(res.data.call_attempts || 0);
                  
                  // Refresh additional data as needed
                  // Refresh notes history
                  try {
                    const notesRes = await axios.get(`/api/leads/${id}/notes-history`, config);
                    if (notesRes.data && Array.isArray(notesRes.data)) {
                      setNotesHistory(notesRes.data);
                    }
                  } catch (notesErr) {
                    console.log('Notes history not available', notesErr);
                  }
                  
                  setLoading(false);
                } catch (err) {
                  console.error('Error refreshing lead:', err);
                  setLoading(false);
                }
              };
              
              fetchLead();
            }
          }}
          lead={lead}
        />
      )}

      {/* Email View Modal */}
      <EmailViewModal
        isOpen={emailViewModalOpen}
        onClose={() => setEmailViewModalOpen(false)}
        email={selectedEmail}
        onReply={handleReplyToEmail}
      />
      
      {/* View/Edit Deal Modal */}
      <ViewEditDealModal
        isOpen={viewDealModalOpen}
        onClose={() => setViewDealModalOpen(false)}
        onDealUpdated={() => {
          // Refresh deal history after a deal is updated
          const fetchUpdatedDealHistory = async () => {
            try {
              const token = localStorage.getItem('token');
              
              const config = {
                headers: {
                  'x-auth-token': token
                }
              };
              
              // Refresh deal count and deals
              fetchDealCount();
              fetchDealsForLead();
              
              // Refresh deal history
              const dealRes = await axios.get(`/api/deals/lead/${id}/history`, config);
              if (dealRes.data && Array.isArray(dealRes.data)) {
                setDealHistory(dealRes.data);
              }
              
              // Refresh deal stage history
              const dealStageRes = await axios.get(`/api/deals/lead/${id}/stage-history`, config);
              if (dealStageRes.data && Array.isArray(dealStageRes.data)) {
                setDealStageHistory(dealStageRes.data);
              }
            } catch (err) {
              console.log('Error refreshing deal history', err);
            }
          };
          
          fetchUpdatedDealHistory();
        }}
        dealId={selectedDealId}
      />
      
      {/* New Task Modal */}
      {newTaskModalOpen && lead && (
        <NewTaskModal 
          isOpen={newTaskModalOpen} 
          onClose={setNewTaskModalOpen} 
          leadId={lead.id}
          leadName={lead.name}
          setTaskHistory={setTaskHistory}
          setActivityPage={setActivityPage}
        />
      )}
      
      {/* Delete Confirmation Modal for Activity Entries */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={executeDeleteAction}
        message={deleteMessage}
        title={`Delete ${deleteType === 'note' ? 'Note' : deleteType === 'email' ? 'Email Record' : deleteType === 'call' ? 'Call Record' : 'Activity Entry'}`}
      />
      
      {/* Error Modal for deletion errors */}
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={() => setShowErrorModal(false)}
        title="Cannot Delete Lead"
        message={errorModalMessage}
        confirmButtonText="OK"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        iconComponent={
          <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        }
      />

      {/* Render the Edit Task Modal */}
      <EditTaskModal
        isOpen={showEditTaskModal}
        onClose={(updated) => {
          setShowEditTaskModal(false);
          setSelectedTaskToEdit(null); // Clear selected task
          // If the task was updated, refresh the task history
          if (updated) {
            fetchTaskHistory(); // Use the shared fetchTaskHistory function
          }
        }}
        onTaskUpdated={(updatedTask) => {
          console.log('Task updated successfully:', updatedTask);
          // We'll refresh the task history after closing the modal via onClose
        }}
        taskToEdit={selectedTaskToEdit}
        users={users} // Pass users list for the assignee dropdown
        // onTaskUpdated is handled by the onClose logic above
      />
      
    </div>
  );
};

// New Task Modal Component
const NewTaskModal = ({ isOpen, onClose, leadId, leadName, setTaskHistory, setActivityPage }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().substr(0, 10); // Format as YYYY-MM-DD
  };

  // Get current user id
  const getCurrentUserId = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const currentUser = JSON.parse(userStr);
      return currentUser.id;
    }
    return '';
  };

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: getTodayDate(), // Set default to today
    due_time: '23:59', // Default to end of day
    priority: 'medium',
    assigned_to: getCurrentUserId(), // Set to current user by default
    lead_id: leadId, // Prefill with current lead ID
    status: 'pending',
    type: 'task' // Default type is task (vs support)
  });

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        const res = await axios.get('/api/auth/users', config);
        setUsers(res.data);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    fetchUsers();
  }, []);
  
  // Add ESC key handler to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        onClose(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    // Clean up event listener
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const handleNewTaskChange = (e) => {
    const { name, value } = e.target;
    setNewTask({
      ...newTask,
      [name]: value
    });
  };

  const handleSubmitNewTask = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Use the real API endpoint
      await axios.post('/api/tasks', newTask, config);
      
      // Reset the form
      setNewTask({
        title: '',
        description: '',
        due_date: getTodayDate(),
        due_time: '23:59',
        priority: 'medium',
        assigned_to: getCurrentUserId(), // Keep assigned to current user
        lead_id: leadId,
        status: 'pending',
        type: 'task'
      });
      
      // Refresh task history
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        const taskRes = await axios.get(`/api/tasks/lead/${leadId}/history`, config);
        if (taskRes.data && Array.isArray(taskRes.data)) {
          setTaskHistory(taskRes.data);
          // Reset to first page when new task is added
          setActivityPage(1);
        }
      } catch (taskErr) {
        console.log('Error refreshing task history', taskErr);
      }
      
      setLoading(false);
      setError('');
      onClose(false); // Close the modal
      
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task. Please try again.');
      setLoading(false);
    }
  };

  // If the modal is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div 
        className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        onClick={(e) => {
          // Close if clicking outside the modal
          if (e.target === e.currentTarget) {
            onClose(false);
          }
        }}
      >
        <div 
          className="fixed inset-0 transition-opacity" 
          aria-hidden="true"
          onClick={() => onClose(false)}
        >
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Create New Task for {leadName}</h3>
                
                {error && (
                  <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4 text-sm text-red-700">
                    {error}
                  </div>
                )}
                
                <form onSubmit={handleSubmitNewTask}>
                  <div className="mb-4">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      id="title"
                      required
                      value={newTask.title}
                      onChange={handleNewTaskChange}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows="3"
                      value={newTask.description}
                      onChange={handleNewTaskChange}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                    ></textarea>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-1">
                        Due Date & Time
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          name="due_date"
                          id="due_date"
                          required
                          value={newTask.due_date}
                          onChange={handleNewTaskChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                        />
                        <input
                          type="time"
                          name="due_time"
                          id="due_time"
                          required
                          value={newTask.due_time}
                          onChange={handleNewTaskChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <select
                        name="priority"
                        id="priority"
                        value={newTask.priority}
                        onChange={handleNewTaskChange}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="assigned_to" className="block text-sm font-medium text-gray-700 mb-1">
                        Assign To
                      </label>
                      <select
                        name="assigned_to"
                        id="assigned_to"
                        value={newTask.assigned_to}
                        onChange={handleNewTaskChange}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                      >
                        <option value="">Select a user</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>{user.name}{user.id === getCurrentUserId() ? ' (you)' : ''}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        name="type"
                        id="type"
                        value={newTask.type}
                        onChange={handleNewTaskChange}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                      >
                        <option value="task">Sales Task</option>
                        <option value="support">Support Ticket</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating...
                        </>
                      ) : 'Create Task'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onClose(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadView;
